import type { DictFragment } from '../translations';

/** Accueil (home page): my tasks by project, the projects I sponsor. Owns the `home.*` keys. */
export const home: DictFragment = {
  fr: {
    'home.hello': 'Bonjour {name}',
    'home.helloAnon': 'Bonjour',
    'home.you': 'vous',
    'home.team': 'Équipe',
    'home.budget': 'Budget',
    'home.budgetBoth': '{available} disponible / {requested} demandé',
    'home.openProject': 'Ouvrir le projet',
    'home.noGoal': 'L’objectif du projet n’est pas encore décrit.',
    'home.goalMore': 'Lire la suite',
    'home.goalLess': 'Réduire',

    'home.day.sun': 'Dim',
    'home.day.mon': 'Lun',
    'home.day.tue': 'Mar',
    'home.day.wed': 'Mer',
    'home.day.thu': 'Jeu',
    'home.day.fri': 'Ven',
    'home.day.sat': 'Sam',
    'home.when.today': 'aujourd’hui, {time}',
    'home.when.yesterday': 'hier, {time}',

    // Mes tâches
    'home.tasks.title': 'Mes tâches',
    'home.tasks.countOne': '{n} tâche ouverte dans {projects} projet',
    'home.tasks.countMany': '{n} tâches ouvertes dans {projects} projets',
    'home.tasks.late': '{n} en retard',
    'home.tasks.calendar': 'Les dates dans Mon calendrier',
    'home.tasks.todos': '{done}/{total} sous-tâches',
    'home.tasks.with': 'avec {names}',
    'home.tasks.status': 'Statut de « {title} »',
    'home.tasks.markDone': 'Marquer comme faite',
    'home.tasks.done': 'Fait',
    'home.tasks.notResponsible': 'Seul·e le ou la responsable de la tâche en change le statut',
    'home.tasks.moreOne': 'Afficher l’autre tâche',
    'home.tasks.moreMany': 'Afficher les {n} autres tâches',
    'home.tasks.fewer': 'Réduire',
    'home.tasks.mention': '{name} vous a tagué·e · {when}',
    'home.tasks.meeting': 'Séance « {title} » · {when}',
    'home.tasks.showAll': 'Afficher les {n} autres projets',
    'home.tasks.showFewer': 'Afficher moins de projets',

    // Mes projets sponsorisés
    'home.sponsored.title': 'Mes projets sponsorisés',
    'home.sponsored.watch': '{n} à surveiller',
    'home.sponsored.milestones': '{done}/{total} jalons',
    'home.sponsored.running': 'En cours',
    'home.sponsored.planning': 'Planifiés',
    'home.sponsored.preparing': 'En préparation',
    'home.sponsored.blocked': 'Bloqués',
    'home.stages.title': 'Parcours du projet',
    'home.stages.noPlanYet': 'Le planning se fait à l’étape Planification.',

    // The planning axis
    'home.spine.title': 'Planning',
    'home.spine.legendUpcoming': 'Séance à venir',
    'home.spine.barLabel': 'J{n} · {name}',
    'home.spine.barTitle': 'Jalon {n} · {name} — {done}/{total} tâches faites',
    'home.spine.undated': 'Jalons sans dates :',
    'home.spine.noMilestones': 'Le planning n’a pas encore de jalons.',

    // The milestone in focus
    'home.task.late': 'En retard',
    'home.deliv.validate': 'Valider',
    'home.deliv.unvalidate': 'Retirer la validation',
    'home.deliv.validated': 'Validé',
    'home.deliv.open': 'À valider',
    'home.deliv.validatedBy': 'validé par {name} le {date}',
    'home.deliv.count': '{done}/{total} validés',

    // Who does what
    'home.who.title': 'Qui fait quoi',
    'home.who.empty': 'Personne n’est encore dans l’équipe.',
    'home.who.openOne': '{n} ouverte',
    'home.who.openMany': '{n} ouvertes',
    'home.who.noRole': 'Rôle non précisé',
    'home.who.nothing': 'Rien en cours',
    'home.who.privateTask': 'Tâche privée',
    'home.who.peopleOne': '{n} personne',
    'home.who.peopleMany': '{n} personnes',
    'home.who.more': '+ {n} autres',

    // Meetings and news
    'home.meetings.title': 'Séances',
    'home.meetings.none': 'Aucune séance.',
    'home.meetings.noneUpcoming': 'Aucune séance à venir.',
    'home.meetings.all': 'Chronologie',
    'home.meetings.untitled': 'Séance',
    'home.news.title': 'Quoi de neuf',
    'home.news.none': 'Rien de nouveau.',
    'home.news.comment': 'a commenté le projet',
    'home.news.reply': 'a répondu sur le projet',
    'home.news.commentOnTask': 'a commenté « {task} »',
    'home.news.replyOnTask': 'a répondu sur « {task} »',
    'home.news.all': 'Tout l’historique',
    'home.event.deliverableValidated': 'Livrable « {name} » validé',
    'home.event.deliverableUnvalidated': 'Validation du livrable « {name} » retirée',

    'home.empty.title': 'Rien à suivre pour l’instant',
    'home.empty.desc': 'Vos tâches et les projets dont vous êtes sponsor apparaîtront ici.',
    'home.empty.portfolio': 'Voir le portefeuille',
    'home.empty.submit': 'Soumettre un projet',
  },
  en: {
    'home.hello': 'Hello {name}',
    'home.helloAnon': 'Hello',
    'home.you': 'you',
    'home.team': 'Team',
    'home.budget': 'Budget',
    'home.budgetBoth': '{available} available / {requested} requested',
    'home.openProject': 'Open project',
    'home.noGoal': 'The project’s goal isn’t described yet.',
    'home.goalMore': 'Read more',
    'home.goalLess': 'Show less',

    'home.day.sun': 'Sun',
    'home.day.mon': 'Mon',
    'home.day.tue': 'Tue',
    'home.day.wed': 'Wed',
    'home.day.thu': 'Thu',
    'home.day.fri': 'Fri',
    'home.day.sat': 'Sat',
    'home.when.today': 'today, {time}',
    'home.when.yesterday': 'yesterday, {time}',

    'home.tasks.title': 'My tasks',
    'home.tasks.countOne': '{n} open task in {projects} project',
    'home.tasks.countMany': '{n} open tasks in {projects} projects',
    'home.tasks.late': '{n} overdue',
    'home.tasks.calendar': 'Dates in My calendar',
    'home.tasks.todos': '{done}/{total} to-dos',
    'home.tasks.with': 'with {names}',
    'home.tasks.status': 'Status of “{title}”',
    'home.tasks.markDone': 'Mark as done',
    'home.tasks.done': 'Done',
    'home.tasks.notResponsible': 'Only the task’s responsible changes its status',
    'home.tasks.moreOne': 'Show the other task',
    'home.tasks.moreMany': 'Show the {n} other tasks',
    'home.tasks.fewer': 'Show fewer',
    'home.tasks.mention': '{name} tagged you · {when}',
    'home.tasks.meeting': 'Meeting “{title}” · {when}',
    'home.tasks.showAll': 'Show the {n} other projects',
    'home.tasks.showFewer': 'Show fewer projects',

    'home.sponsored.title': 'Projects I sponsor',
    'home.sponsored.watch': '{n} to watch',
    'home.sponsored.milestones': '{done}/{total} milestones',
    'home.sponsored.running': 'Under way',
    'home.sponsored.planning': 'Planned',
    'home.sponsored.preparing': 'Being prepared',
    'home.sponsored.blocked': 'Blocked',
    'home.stages.title': 'Project path',
    'home.stages.noPlanYet': 'The plan is made at the Planning stage.',

    'home.spine.title': 'Planning',
    'home.spine.legendUpcoming': 'Upcoming meeting',
    'home.spine.barLabel': 'M{n} · {name}',
    'home.spine.barTitle': 'Milestone {n} · {name} — {done}/{total} tasks done',
    'home.spine.undated': 'Milestones without dates:',
    'home.spine.noMilestones': 'The plan has no milestones yet.',

    'home.task.late': 'Overdue',
    'home.deliv.validate': 'Validate',
    'home.deliv.unvalidate': 'Withdraw validation',
    'home.deliv.validated': 'Validated',
    'home.deliv.open': 'To validate',
    'home.deliv.validatedBy': 'validated by {name} on {date}',
    'home.deliv.count': '{done}/{total} validated',

    'home.who.title': 'Who does what',
    'home.who.empty': 'Nobody is on the team yet.',
    'home.who.openOne': '{n} open',
    'home.who.openMany': '{n} open',
    'home.who.noRole': 'Role not set',
    'home.who.nothing': 'Nothing under way',
    'home.who.privateTask': 'Private task',
    'home.who.peopleOne': '{n} person',
    'home.who.peopleMany': '{n} people',
    'home.who.more': '+ {n} more',

    'home.meetings.title': 'Meetings',
    'home.meetings.none': 'No meeting.',
    'home.meetings.noneUpcoming': 'No upcoming meeting.',
    'home.meetings.all': 'Timeline',
    'home.meetings.untitled': 'Meeting',
    'home.news.title': 'What’s new',
    'home.news.none': 'Nothing new.',
    'home.news.comment': 'commented on the project',
    'home.news.reply': 'replied on the project',
    'home.news.commentOnTask': 'commented on “{task}”',
    'home.news.replyOnTask': 'replied on “{task}”',
    'home.news.all': 'Full history',
    'home.event.deliverableValidated': 'Deliverable “{name}” validated',
    'home.event.deliverableUnvalidated': 'Deliverable “{name}” validation withdrawn',

    'home.empty.title': 'Nothing to follow yet',
    'home.empty.desc': 'Your tasks and the projects you sponsor will show here.',
    'home.empty.portfolio': 'See the portfolio',
    'home.empty.submit': 'Submit a project',
  },
};
